import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import { useAuth } from "@/context/AuthContext";

const API = `${BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [breached, setBreached] = useState(false);
  const navigate = useNavigate();
  const { setAuthFromReset } = useAuth();

  useEffect(() => {
    if (!token) toast.error("No reset token in link. Request a new email.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error("Password must be at least 6 characters");
    if (newPassword !== confirm) return toast.error("Passwords don't match");
    if (breached) return toast.error("This password appears in known breaches — pick another.");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      // Backend returns {ok, token, user} — sign the user in immediately for a friction-free flow.
      if (r.data.token && setAuthFromReset) setAuthFromReset(r.data.token, r.data.user);
      setDone(true);
      toast.success("Password updated. You're logged in.");
      setTimeout(() => navigate("/studio"), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. Request a new link.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="w-full max-w-md rounded-2xl border border-[#2A354B] bg-[#121620] p-8 space-y-6">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF] flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-[#00F0FF]"/>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">// Password reset</div>
              <div className="font-display font-black text-2xl">Set a new password</div>
            </div>
          </div>
          <p className="text-sm text-[#94A3B8]">Enter your new password. You'll be signed in automatically once we save it.</p>
        </div>

        {done ? (
          <div className="space-y-3" data-testid="reset-done">
            <div className="p-4 rounded border border-[#10B981]/50 bg-[#10B981]/10 text-sm text-[#F8FAFC]">
              Password updated. Redirecting to the studio…
            </div>
            <Link to="/studio" className="block text-center text-xs font-mono uppercase tracking-widest text-[#00F0FF]">Go now →</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#94A3B8]">New password</Label>
              <PasswordInput testId="reset-new-password" required minLength={6}
                value={newPassword} onChange={e => setNewPassword(e.target.value)}/>
              <PasswordStrengthMeter password={newPassword} testId="reset-pw-strength" onBreachChange={setBreached}/>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#94A3B8]">Confirm password</Label>
              <PasswordInput testId="reset-confirm-password" required minLength={6}
                value={confirm} onChange={e => setConfirm(e.target.value)}/>
            </div>
            <Button data-testid="reset-submit-button" type="submit" disabled={busy || !token || breached}
              className="w-full bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Update password"}
            </Button>
            <div className="text-center text-xs text-[#94A3B8]">
              <Link to="/" className="text-[#00F0FF] font-bold uppercase tracking-widest">← Back to home</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
